import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  GridItem,
  Heading,
  Text,
  Image,
  HStack,
  VStack,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagLeftIcon,
  Divider,
  Button,
  Link,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { FiAward, FiExternalLink, FiShield, FiCheckCircle } from "react-icons/fi";

export default function WhoWeAre() {
  // --- keep: certifications (skills) ---
  const certifications = [
    { code: "CEHv10", long: "Certified Ethical Hacker" },
    { code: "MCRTA", long: "Multi-Cloud Red Team Analyst" },
    { code: "ISO 27001 LI", long: "ISO 27001 Lead Implementer" },
    { code: "ISO 27701 LI", long: "ISO 27701 Lead Implementer" },
    { code: "DORA LM", long: "DORA Lead Manager" },
  ];
  
  const BOOKING_URL = "https://calendly.com/rowan-gemini-cybersecurity/30min";

  // Merged “Our approach” (now inside the main text block)
  const approach = [
    "Scope quickly to minimise noise.",
    "Prioritise manual testing where it matters.",
    "Deliver business-first reporting.",
    "Provide clear, fast remediation steps.",
    "Re-test to prove closure.",
  ];

  // Focus areas (shown at the foot of the text block)
  const focusAreas = [
    "Network & Cloud Penetration Testing",
    "Web Application/API & Mobile",
    "Wi-Fi Penetration Testing",
    "ISO 27001 / 27701",
    "SOC 2 readiness",
  ];
  
  const glowColor = "#00E5FF"; // change this to any hex you like
  
  const images = [
  { src: "/cert_1.png", alt: "ISO 27001", glow: "#00E5FF" },
  { src: "/cert_2.png", alt: "DORA", glow: "#FF3CAC" },
  { src: "/cert_3.png", alt: "ISO 27701", glow: "#39FF14", /*wide: true*/},
  ];

  return (
    <Box as="section" id="who-we-are" bg="black" color="white" pt={{ base: 16, md: 24 }} pb={{ base: 8, md: 10 }}>
      <Container maxW="7xl" px={{ base: 6, md: 8 }}>
        {/* Small label + big headline */}
        <VStack spacing={2} textAlign="left" align="start">
          {/* smaller but above */}
          <Heading
            as="p"
            size="xl"
            color="#bd00ff"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            Who we are
          </Heading>

          {/* Main statement — change as/size if you want h1/h3 later */}
          <Heading as="h3" size={{ base: "lg", md: "xl" }} lineHeight="1.2" fontFamily="'Poppins', ui-sans-serif, system-ui" fontWeight="300">
            We are a specialist offensive security and compliance team. We help you{" "}
            <Box as="span" fontWeight="extrabold">
              prevent breaches
            </Box>{" "}
            and{" "}
            <Box as="span" fontWeight="extrabold">
              prove compliance
            </Box>{" "}
            with practical, hands-on testing and clear guidance.
          </Heading>
        </VStack>

        {/* Images LEFT, all text (incl. approach & focus areas) RIGHT */}
        {/* Top alignment fix: alignItems="start" and each column alignSelf="start" */}
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          gap={10}
          mt={10}
          alignItems="start"            // ⬅️ top-align the grid items
        >
          {/* IMAGES — left on lg+ */}
		  <GridItem order={{ base: 2, lg: 1 }} alignSelf="start">
		    <SimpleGrid columns={2} gap={4}>
		      {images.map((img) => {
			const glowFilter = `
			  drop-shadow(0 0 3px ${img.glow}33)
			  drop-shadow(0 0 6px ${img.glow}33)
			  drop-shadow(0 0 12px ${img.glow}44)
			  drop-shadow(0 0 22px ${img.glow}55)
			`;
			const ImgEl = (
			  <Image
			    src={img.src}
			    alt={img.alt}
			    borderRadius="xl"
			    objectFit="cover"
			    loading="lazy"
			    // Transparent background, the glow is on the image:
			    bg="transparent"
			    // This makes the glow match the image’s actual shape (PNG alpha)
			    filter={glowFilter}
			  />
			);
			return img.wide ? (
			  <GridItem key={img.src} colSpan={2}>
			    {ImgEl}
			  </GridItem>
			) : (
			  <Box key={img.src}>{ImgEl}</Box>
			);
		      })}
             </SimpleGrid>
          </GridItem>

          {/* TEXT BLOCK — right on lg+ (includes what we do, approach, focus areas, CTA, then certifications) */}
          <GridItem order={{ base: 1, lg: 2 }} alignSelf="start">
            <VStack align="start" spacing={8}>
              {/* What we do */}
              <Text color="whiteAlpha.900" fontFamily="'Inter', ui-sans-serif, system-ui" fontWeight="400" fontSize={{ base: "lg", md: "xl"}} lineHeight={{ base: "1.7", md: "1.75"}}>
                We deliver high-impact penetration testing, cloud and application security, and
                ISO 27001/27701 implementation support. Our work blends manual adversarial
                techniques with standards-aligned controls to reduce risk and clear audits.
              </Text>

              {/* Our approach (merged) */}
              <Box>
                <Heading as="h4" size="md" mb={2} color="whiteAlpha.900">
                  Our approach
                </Heading>
                <List spacing={2}>
                  {approach.map((step) => (
                    <ListItem key={step}>
                      <ListIcon as={FiCheckCircle} />
                      {step}
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Focus areas (foot of the block) */}
              <Box w="full">
                <Heading as="h4" size="md" mb={4} color="whiteAlpha.900">
                  Focus areas
                </Heading>
                <Wrap spacing={2}>
                  {focusAreas.map((t) => (
                    <WrapItem key={t}>
                      <Tag variant="outline" borderRadius="full">
                        <TagLeftIcon as={FiShield} />
                        <TagLabel>{t}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>

              {/* CTA */}
              <HStack pt={1}>
		  <Link href={BOOKING_URL} isExternal rel="noopener noreferrer">
		    <Button colorScheme="purple" rightIcon={<FiExternalLink />}>
		      Book an appointment
		    </Button>
		  </Link>
		</HStack>
		
              {/* Certifications — now directly below the button */}
              <Box w="full" pt={4}>
                <Heading as="h3" size="md" mb={3}>
                  Certifications
                </Heading>
                <Wrap spacing={2}>
                  {certifications.map((c) => (
                    <WrapItem key={c.code}>
                      <Tag
                        size="lg"
                        variant="outline"
                        borderRadius="full"
                        borderColor="purple.300"
                        color="purple.100"
                        fontWeight="semibold"
                      >
                        <TagLeftIcon as={FiAward} />
                        <TagLabel>{c.code}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                <Text mt={3} fontSize="sm" color="whiteAlpha.700">
                  {certifications.map((c) => c.long).join(" • ")}
                </Text>
              </Box>
            </VStack>
          </GridItem>
        </SimpleGrid>

        {/* keep a light divider under the whole section */}
        <Divider mt={8} mb={0} borderColor="whiteAlpha.200" />
      </Container>
    </Box>
  );
}
